import React from 'react';
import { useTranslation } from 'react-i18next';
import useHardwareFirstMembershipTerms from '../../hooks/useHardwareFirstMembershipTerms';
import SimpleDismissableModal from '../modals/SimpleDismissableModal';
import styles from './HardwareFirstPlanModal.module.scss';

const STEPS = [
  { labelKey: 'hardware_first_step_at_checkout', bodyKey: 'hardware_first_step_checkout_body' },
  { labelKey: 'hardware_first_step_at_activation', bodyKey: 'hardware_first_step_activation_body' },
  { labelKey: 'hardware_first_step_using_fi', bodyKey: 'hardware_first_step_using_fi_body' },
];

/** the term label per renewal length; a term the copy does not know is not rendered */
const TERM_KEYS: Record<number, string> = {
  6: 'hardware_first_plan_term_6m',
  12: 'hardware_first_plan_term_1y',
  24: 'hardware_first_plan_term_2y',
};

interface HardwareFirstPlanModalProps {
  trigger: JSX.Element;
}

/** Explains the activation-time plan choice behind the plp's "Fi GPS Cellular Plan" card. */
export default function HardwareFirstPlanModal({ trigger }: HardwareFirstPlanModalProps) {
  const { t } = useTranslation();
  const terms = useHardwareFirstMembershipTerms();

  return (
    <SimpleDismissableModal trigger={trigger} containerClassName={styles.modal} closeIconClassName={styles.closeIcon}>
      {(close) => (
        <div className={styles.container}>
          <div className={styles.scroll}>
            <div className={styles.hero}>
              <h2 className={styles.heroTitle}>{t('hardware_first_plan_modal_title')}</h2>
            </div>
            <section className={styles.howItWorks}>
              <div className={styles.eyebrow}>{t('hardware_first_how_it_works')}</div>
              <div className={styles.steps}>
                {STEPS.map((step, index) => (
                  <div className={styles.step} key={step.labelKey}>
                    <span className={styles.stepNumber}>{index + 1}</span>
                    <span className={styles.stepText}>
                      <span className={styles.stepLabel}>{t(step.labelKey)}</span>
                      <span className={styles.stepBody}>{t(step.bodyKey)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
            <section className={styles.plans}>
              <p className={styles.plansIntro}>{t('hardware_first_plan_modal_intro')}</p>
              {terms.map((term) => {
                const termKey = TERM_KEYS[term.renewalMonths];
                if (!termKey) {
                  return null;
                }
                return (
                  <div className={styles.planCard} key={term.renewalMonths}>
                    <div className={styles.planTop}>
                      <span className={styles.planTerm}>{t(termKey)}</span>
                      <span className={styles.planPricing}>
                        <span className={styles.planPriceRow}>
                          <span className={styles.planPrice}>{term.intro}</span>
                          <span className={styles.planListPrice}>{term.renewal}</span>
                        </span>
                        <span className={styles.planMonthly}>
                          {t('hardware_first_plan_equal_to_per_month', { amount: term.monthly })}
                        </span>
                      </span>
                    </div>
                    <div className={styles.planDivider} />
                    <div className={styles.planRenewal}>
                      {t('hardware_first_plan_renews_at', { price: term.renewal, term: t(termKey) })}
                    </div>
                  </div>
                );
              })}
            </section>
          </div>
          <div className={styles.ctaBar}>
            <button type="button" className={styles.cta} onClick={close}>
              {t('got_it')}
            </button>
          </div>
        </div>
      )}
    </SimpleDismissableModal>
  );
}
